/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Paper } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  StatisticsTitle,
  StatisticsValue,
  CardContainer,
  IconContainer,
  InformationContainer,
  TitleContainer,
  LightTooltip,
  StyledInfoIcon,
} from "../../../components/statistics_cards/styles";
import { StatisticSkeletalLoader } from "../../../components/statistic_skeletal_loader";
import { getUniformRowCounts, getCardLayoutStyles } from "../../../utils/statCardGridLayout";
import { ComparisonRow, EMPTY_DISPLAY } from "./cells";
import { METRIC_DESCRIPTIONS } from "./metric_copy";

const ICON_COLOR = "#f97316";

const iconFor = (Icon) => <Icon sx={{ color: ICON_COLOR }} />;

const METRIC_ICONS = {
  gross_sales: ReceiptLongOutlinedIcon,
  net_sales: PaymentsOutlinedIcon,
  covers: GroupsOutlinedIcon,
  avg_check: LocalAtmOutlinedIcon,
  promo_percent: PercentOutlinedIcon,
  void_qty: RemoveCircleOutlineIcon,
  labor_percent: BadgeOutlinedIcon,
};

const KIND_ICONS = {
  money: AttachMoneyOutlinedIcon,
  percent: PercentOutlinedIcon,
  count: NumbersOutlinedIcon,
};

const resolveIcon = (metric) =>
  iconFor(METRIC_ICONS[metric?.metric] || KIND_ICONS[metric?.kind] || NumbersOutlinedIcon);

/**
 * Group-level tiles for the business date, one per metric the API sends. Each tile shows
 * Today alongside the SDLW/SDLY comparisons and their variances, printed exactly as received —
 * the `--` placeholder included, which means "no data" and never zero.
 *
 * Laid out with the same uniform-row-count technique as the Dashboard's statistics cards (see
 * statistics_cards/index.jsx). The cards draw the whole frame themselves rather than the grid
 * container carrying a border, so the frame traces their real silhouette and a short trailing
 * row closes off at its own width instead of running on across the empty cells beside it.
 * Every tier is a plain uniform grid, which keeps all tiles one column wide — these tiles hold
 * three lines of figures, so a stretched one reads as broken rather than merely wider.
 */
export const DailyLogbookSummary = ({ metrics = [], loading = false, isMobile = false }) => {
  if (metrics.length === 0) return null;

  const totalMetrics = metrics.length;
  const stackedRowCounts = getUniformRowCounts(totalMetrics, 1);
  const twoColumnRowCounts = getUniformRowCounts(totalMetrics, 2);
  const threeColumnRowCounts = getUniformRowCounts(totalMetrics, 3);
  // Widest tier splits into two rows, but as plain uniform columns rather than the Dashboard's
  // span-filled rows — a short bottom row keeps its cards at one column each instead of
  // stretching them wider than the row above.
  const largeScreenColumnCount = Math.ceil(totalMetrics / 2);
  const largeScreenRowCounts = getUniformRowCounts(totalMetrics, largeScreenColumnCount);

  return (
    <Box
      data-testid="daily-logbook-summary-container"
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
        marginBottom: "20px",
        [theme.breakpoints.up("sm")]: {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        },
        [theme.breakpoints.between(1000, "lg")]: {
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        },
        [theme.breakpoints.up("lg")]: {
          gridTemplateColumns: `repeat(${largeScreenColumnCount}, minmax(0, 1fr))`,
        },
      })}
    >
      {metrics.map((metric, index) => (
        <Paper
          key={metric.metric}
          elevation={0}
          square
          data-testid={`daily-logbook-summary-card-${metric.metric}`}
          sx={(theme) => ({
            backgroundColor: "white",
            padding: isMobile ? "12px" : "10px 20px",
            ...getCardLayoutStyles({
              index,
              rowCounts: stackedRowCounts,
              includeOuterEdges: true,
            }),
            [theme.breakpoints.up("sm")]: {
              ...getCardLayoutStyles({
                index,
                rowCounts: twoColumnRowCounts,
                includeOuterEdges: true,
              }),
            },
            [theme.breakpoints.between(1000, "lg")]: {
              ...getCardLayoutStyles({
                index,
                rowCounts: threeColumnRowCounts,
                includeOuterEdges: true,
              }),
            },
            [theme.breakpoints.up("lg")]: {
              ...getCardLayoutStyles({
                index,
                rowCounts: largeScreenRowCounts,
                includeOuterEdges: true,
              }),
            },
          })}
        >
          <CardContainer isMobile={isMobile}>
            <IconContainer>{resolveIcon(metric)}</IconContainer>
            <InformationContainer isMobile={isMobile}>
              <TitleContainer>
                <StatisticsTitle data-testid={`daily-logbook-summary-title-${metric.metric}`}>
                  {metric.label}
                </StatisticsTitle>
                <LightTooltip title={METRIC_DESCRIPTIONS[metric.metric]} placement="top">
                  <StyledInfoIcon />
                </LightTooltip>
              </TitleContainer>
              {loading ? (
                <StatisticSkeletalLoader dataTestId={`daily-logbook-summary-loader-${metric.metric}`} />
              ) : (
                <React.Fragment>
                  <StatisticsValue
                    data-testid={`daily-logbook-summary-value-${metric.metric}`}
                    sx={{ fontSize: "18px" }}
                  >
                    {metric?.today?.display ?? EMPTY_DISPLAY}
                  </StatisticsValue>
                  <ComparisonRow
                    label="SDLW"
                    cell={metric.sdlw}
                    varianceCell={metric.var_sdlw}
                    testId={`daily-logbook-summary-sdlw-${metric.metric}`}
                  />
                  <ComparisonRow
                    label="SDLY"
                    cell={metric.sdly}
                    varianceCell={metric.var_sdly}
                    testId={`daily-logbook-summary-sdly-${metric.metric}`}
                  />
                </React.Fragment>
              )}
            </InformationContainer>
          </CardContainer>
        </Paper>
      ))}
    </Box>
  );
};
